import taapi from './taapi'
import type { Project, PmInitResult, BatchProcessArgs, ExtractedQuestion, ExtractQuestionsWebResult } from '../types'
import { addToast } from './Toasts'

// ─── Projects ─────────────────────────────────────────────────────────────────

export type { PmInitResult, BatchProcessArgs as PmBatchProcessArgs }

export async function pmInit(
  page: number,
  order: string,
  asc: boolean,
  pagesize: number,
  searchname?: string,
): Promise<PmInitResult> {
  return taapi.post('/api2/pminit', { page, order, asc, pagesize, searchname: searchname || undefined })
}

export async function pmGetProject(projectsha: string | undefined): Promise<Project | null> {
  return taapi.post('/api2/pmgetproject', { projectsha })
}

export async function pmAddProject(projectname: string): Promise<void> {
  await taapi.post('/api2/pmaddproject', { projectname })
}

export async function pmDelProject(projectsha: string | undefined): Promise<void> {
  await taapi.post('/api2/pmdelproject', { projectsha })
}

// ─── Questions ────────────────────────────────────────────────────────────────

export async function pmSaveQuestion(fields: Record<string, any> & { projectsha: string }): Promise<any> {
  return taapi.post('/api2/pmsavequestion', fields)
}

export async function pmDelQuestion(questionsha: string, projectsha: string): Promise<void> {
  await taapi.post('/api2/pmdelquestion', { questionsha, projectsha })
}

export async function pmSaveLocks(questionsha: string, projectsha: string, locks: Record<string, boolean>): Promise<void> {
  await taapi.post('/api2/pmsavelocks', { questionsha, projectsha, locks })
}

// ─── Project mutations ────────────────────────────────────────────────────────

export async function pmSaveProject(project: Project | null): Promise<Project | null> {
  if (!project) return null;
  try {
    const result = await taapi.post('/api2/pmsaveproject', project)
    return result;
  } catch {
    addToast('error', 'Bid Save Failed')
  }
  return null;
}

export async function pmReport(
  project: Project | null,
  reporttypestring: string,
): Promise<{ doc: string; filename: string }> {
  return taapi.post('/api2/pmreport', { project, reporttypestring })
}

// ─── Question extraction / import ─────────────────────────────────────────────

export async function pmExtractQuestions(args: {
  file: File
  projectsha?: string
  dirsha?: string
  splitdoc?: boolean
  customextractprompt?: string
}): Promise<ExtractQuestionsWebResult | undefined> {
  const fd = new FormData()
  fd.append('doc', args.file)
  fd.append('dirsha', args.dirsha ?? '')
  fd.append('projectsha', args.projectsha ?? '')
  fd.append('splitdoc', args.splitdoc ? 'true' : 'false')
  if (args.customextractprompt) fd.append('customextractprompt', args.customextractprompt)
  return taapi.postForm('/api2/pmextractquestions', fd)
}

export async function pmImportProject(args: {
  projectsha: string
  projecttext?: string
  questions?: ExtractedQuestion[]
}): Promise<{ success: boolean; error?: string; message?: string } | undefined> {
  if (!args.projectsha) return undefined;
  return taapi.post('/api2/pmimportproject', args)
}

// ─── Project history (rollback) ───────────────────────────────────────────────

export interface ProjectHistoryRow {
  projectid: number
  version: number
  orgid: number
  // projectjson is intentionally omitted from list responses - call
  // pmGetProjectHistoryVersion to load a single version's full snapshot.
  versionsaved: string
}

export interface ProjectHistoryListResult {
  success: boolean
  msg?: string
  versions: ProjectHistoryRow[]
  total: number
  page: number
  pagesize: number
}

export interface ProjectHistorySnapshot {
  project: Partial<Project> | null
  questions: any[]
}

export interface ProjectHistoryVersionResult {
  success: boolean
  msg?: string
  version: number
  snapshot: ProjectHistorySnapshot | null
}

export async function pmGetProjectHistory(
  projectsha: string,
  page = 0,
  pagesize = 20,
): Promise<ProjectHistoryListResult | null> {
  if (!projectsha) return null
  return taapi.post('/api2/pmgethistory', { projectsha, page, pagesize })
}

export async function pmGetProjectHistoryVersion(
  projectsha: string,
  version: number,
): Promise<Project | null> {
  if (!projectsha) return null
  return taapi.post('/api2/pmgethistoryversion', { projectsha, version })
}
